import { Inject, Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { AuthState } from '../store/auth/auth.state';
import { NotificationVisitService } from './notification-visit.service';

export interface IDownloadInteractionService {
  hasInteractedWithDownloadDialog(): boolean;
  setDownloadDialogInteracted(): void;
  setDownloadAction(action: DownloadAction): void;
}

type DownloadAction = 'desktop-app' | 'browser-extension';
type TooltipInteractionKey = 'interacted-desktop-app-tooltip' | 'interacted-browser-extension-tooltip';

@Injectable({
  providedIn: 'root',
})
export class DownloadInteractionService implements IDownloadInteractionService {
  private readonly dialogInteractionKey = 'download-dialog-interacted';
  private readonly desktopAppKey = 'download-action-desktop-app';
  private readonly browserExtensionKey = 'download-action-browser-extension';
  private readonly desktopAppTooltipKey = 'interacted-desktop-app-tooltip';
  private readonly browserExtensionTooltipKey = 'interacted-browser-extension-tooltip';

  constructor(
    @Inject(Store) private readonly store: Store,
    private readonly notificationVisitService: NotificationVisitService,
  ) {}

  hasInteractedWithDownloadDialog(): boolean {
    return this.hasInteractedWithKey(this.dialogInteractionKey);
  }

  setDownloadDialogInteracted(): void {
    this.setInteractionForKey(this.dialogInteractionKey);
  }

  setDownloadAction(action: DownloadAction): void {
    const actionKey = `download-action-${action}`;
    this.setInteractionForKey(actionKey);
  }

  setTooltipInteraction(tooltipKey: TooltipInteractionKey): void {
    this.setInteractionForKey(tooltipKey);
  }

  getDownloadDialogInteracted(): boolean {
    return this.hasInteractedWithKey(this.dialogInteractionKey);
  }

  interactedWithNotification(): boolean {
    return this.notificationVisitService.visitedNotification('calloutdownload-notification-bubble');
  }

  interactedWithDesktopApp(): boolean {
    return this.hasInteractedWithKey(this.desktopAppKey) || this.hasTooltipInteraction(this.desktopAppTooltipKey);
  }

  interactedWithBrowserExtension(): boolean {
    return this.hasInteractedWithKey(this.browserExtensionKey) || this.hasTooltipInteraction(this.browserExtensionTooltipKey);
  }

  hasInteractedWithDownloadAction(): boolean {
    return (this.hasInteractedWithKey(this.desktopAppKey) || this.hasTooltipInteraction(this.desktopAppTooltipKey)) &&
    (this.hasInteractedWithKey(this.browserExtensionKey) || this.hasTooltipInteraction(this.browserExtensionTooltipKey));
  }

  private hasTooltipInteraction(tooltipKey: TooltipInteractionKey): boolean {
    return this.hasInteractedWithKey(tooltipKey);
  }

  private hasInteractedWithKey(key: string): boolean {
    return this.isInteractedInLocalStorage(key) || this.isInteractedInUserSettings(key);
  }

  private setInteractionForKey(key: string): void {
    if (this.hasInteractedWithKey(key)) return;

    this.updateLocalStorage(key, true);
    this.notificationVisitService.setNotificationVisit(key);
  }

  private isInteractedInLocalStorage(key: string): boolean {
    return localStorage.getItem(key) === 'true';
  }

  private isInteractedInUserSettings(key: string): boolean {
    const userSettings = this.store.selectSnapshot(AuthState.company)?.userSettings;
    return userSettings?.custom?.visitedNotification?.[key] || false;
  }

  private updateLocalStorage(key: string, value: boolean): void {
    localStorage.setItem(key, String(value));
  }
}
